import React, { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import DataTable from 'components/ui/data-table';
import LoadingState from '../../shared/LoadingState';

const Pct = ({ value }) => (
  <span>{typeof value === 'number' ? `${value.toFixed(0)}%` : '—'}</span>
);

export default function AcquisitionTab({ conversionPaths, acquisitionFunnel, traffic, loading }) {
  const segmentColumns = useMemo(() => [
    { accessorKey: 'first_landing_class', header: 'First landing' },
    { accessorKey: 'first_source', header: 'First source', meta: { cellClassName: 'text-token-sm' } },
    { accessorKey: 'devices', header: 'Devices', meta: { numeric: true } },
    {
      accessorKey: 'same_day_pct',
      header: 'Same-day',
      meta: { numeric: true },
      cell: ({ row }) => <Pct value={row.original.same_day_pct} />,
    },
    {
      accessorKey: 'median_days_to_signup',
      header: 'Median days',
      meta: { numeric: true },
    },
    {
      accessorKey: 'content_touched_pct',
      header: 'Content-touched',
      meta: { numeric: true },
      cell: ({ row }) => <Pct value={row.original.content_touched_pct} />,
    },
    {
      accessorKey: 'subscribed_pct',
      header: 'Subscribed',
      meta: { numeric: true },
      cell: ({ row }) => <Pct value={row.original.subscribed_pct} />,
    },
    {
      accessorKey: 'revenue_gbp',
      header: 'Revenue £',
      meta: { numeric: true },
      cell: ({ row }) => `£${row.original.revenue_gbp.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
    },
  ], []);

  const pathColumns = useMemo(() => [
    { accessorKey: 'path', header: 'Path (landing classes, in order)', meta: { cellClassName: 'font-mono text-token-sm' } },
    { accessorKey: 'devices', header: 'Devices', meta: { numeric: true } },
  ], []);

  const trafficColumns = useMemo(() => [
    { accessorKey: 'source', header: 'Source' },
    { accessorKey: 'medium', header: 'Medium' },
    { accessorKey: 'sessions', header: 'Sessions', meta: { numeric: true } },
    { accessorKey: 'unique_users', header: 'Users', meta: { numeric: true } },
    { accessorKey: 'new_users', header: 'New devices', meta: { numeric: true } },
    { accessorKey: 'engaged_sessions', header: 'Engaged', meta: { numeric: true } },
  ], []);

  // Weekly funnel: aggregate across landing classes per week
  const weeklyData = useMemo(() => {
    const byWeek = {};
    (acquisitionFunnel?.data || []).forEach((c) => {
      const w = byWeek[c.week] || { week: c.week, new_devices: 0, signup_7d: 0 };
      w.new_devices += c.new_devices;
      w.signup_7d += c.signup_7d;
      byWeek[c.week] = w;
    });
    return Object.values(byWeek)
      .sort((a, b) => a.week.localeCompare(b.week))
      .map((w) => ({
        ...w,
        signup_rate: w.new_devices ? +(100 * (w.signup_7d / w.new_devices)).toFixed(1) : 0,
      }));
  }, [acquisitionFunnel]);

  if (!conversionPaths && loading !== false) {
    return <LoadingState message="Loading acquisition data..." />;
  }

  const cp = conversionPaths || {};

  return (
    <div className="w-full min-w-0">
      {/* Converter cohort paths */}
      <div className="mb-4">
        <h3 className="mb-1 text-token-xl font-semibold">Converter Journeys</h3>
        <p className="mb-2 text-token-xs text-muted-foreground">
          One row per first-touch segment of devices that eventually registered.{' '}
          {cp.total_devices?.toLocaleString() || 0} signup devices, {cp.identified_devices?.toLocaleString() || 0} linked
          to a wp_id (revenue joins cover only those). Stripe-return self-referrals excluded from attribution.
        </p>
        <DataTable
          columns={segmentColumns}
          data={cp.segments || []}
          maxHeight={380}
          emptyMessage="No conversion paths yet — the web-analytics pipeline rebuilds wa_conversion_paths nightly."
        />
      </div>

      {/* Histogram + weekly funnel */}
      <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
        {(cp.histogram || []).length > 0 && (
          <div className="rounded-token-large border border-borderGrey bg-white p-4">
            <h3 className="mb-2 text-token-xl font-semibold">Days to Registration</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={cp.histogram}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#DADADA" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="devices" fill="#254A73" name="Devices" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <p className="mt-1 text-token-xs text-muted-foreground">
              Same-day converters arrive intending to buy; multi-day converters are the content-nurtured
              cohort — the one SEO can grow.
            </p>
          </div>
        )}

        {weeklyData.length > 1 && (
          <div className="rounded-token-large border border-borderGrey bg-white p-4">
            <h3 className="mb-2 text-token-xl font-semibold">
              Weekly New Devices → Signup
              <span className="ml-2 text-token-xs font-normal text-muted-foreground">
                fixed last-8-weeks window (ignores filter bar)
              </span>
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={weeklyData}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#DADADA" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" unit="%" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="new_devices" fill="#254A73" name="New devices" radius={[3, 3, 0, 0]} />
                <Bar yAxisId="right" dataKey="signup_rate" fill="#ff9800" name="7d signup %" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Top literal paths */}
      {(cp.top_paths || []).length > 0 && (
        <div className="mb-4">
          <h3 className="mb-2 text-token-xl font-semibold">Most Common Paths to Registration</h3>
          <DataTable
            columns={pathColumns}
            data={cp.top_paths}
            maxHeight={320}
            emptyMessage="No paths."
          />
        </div>
      )}

      {/* Traffic sources (cleaned) */}
      <div>
        <h3 className="mb-2 text-token-xl font-semibold">
          Traffic Sources
          <span className="ml-2 text-token-xs font-normal text-muted-foreground">
            self-referrals (Stripe returns, own domains) excluded
          </span>
        </h3>
        <DataTable
          columns={trafficColumns}
          data={traffic || []}
          maxHeight={380}
          emptyMessage="No traffic source data."
        />
      </div>
    </div>
  );
}
